import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { shortDigest, type Approval, type ScopeVersion } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { diffScopes, scopeStatus, type ScopeStanding, type ScopeStatus } from '../../lib/scopes';
import { useAcrossEngagements } from '../../lib/useAcrossEngagements';

const STANDING: Record<ScopeStanding, { label: string; cls: string }> = {
  APPROVED: { label: 'Approved', cls: 'status-done' },
  PENDING: { label: 'Awaiting approval', cls: 'status-waiting' },
  DECLINED: { label: 'Declined', cls: 'status-active' },
  SUPERSEDED: { label: 'Superseded', cls: 'status-muted' },
  EXPIRED: { label: 'Expired', cls: 'status-muted' },
  DRAFT: { label: 'Draft', cls: 'status-muted' },
};

const INTEGRITY: Record<ScopeStatus['integrity'], { label: string; cls: string; title: string }> = {
  verified: {
    label: 'Unchanged since approval',
    cls: 'integrity-ok',
    title: 'The stored scope still hashes to the digest the approver recorded.',
  },
  changed: {
    label: 'Changed since approval',
    cls: 'integrity-bad',
    title: 'The stored scope no longer hashes to the digest the approver recorded.',
  },
  unrecorded: {
    label: 'Not hash-pinned',
    cls: 'integrity-none',
    title: 'Approved before approvals recorded a digest, so it cannot be checked.',
  },
  'not-approved': { label: '—', cls: 'integrity-none', title: 'Nothing approved to check.' },
};

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const windowSummary = (scope: ScopeVersion) => {
  const [first] = scope.windows;
  if (!first) return '—';
  const more = scope.windows.length > 1 ? ` (+${scope.windows.length - 1})` : '';
  return `${date(first.startsAt)} → ${date(first.endsAt)}${more}`;
};

const who = (a: Approval) => a.approverEmail ?? `user ${a.approverUserId.slice(0, 8)}`;

/**
 * Every scope version across the tenant: what it authorised, who decided it, and whether the
 * stored scope still matches what they decided on.
 *
 * Scopes are hash-pinned, not signed. The approval records a SHA-256 of the scope it was about,
 * and the server re-hashes the stored scope on every read; a mismatch means the scope changed
 * after approval. That shows a change happened — it does not prove who made it.
 */
export function ScopesPage() {
  const [open, setOpen] = useState<string | null>(null);

  const scopes = useAcrossEngagements<ScopeVersion>('scope', api.listScopeVersions);
  const approvals = useAcrossEngagements<Approval>('approvalHistory', api.listApprovals);

  const rows = scopes.items
    .map(({ item, engagement }) => ({
      scope: item,
      engagement,
      status: scopeStatus(
        item,
        engagement,
        approvals.items.filter((a) => a.engagement.id === engagement.id).map((a) => a.item),
      ),
      previous: scopes.items.find(
        (s) => s.engagement.id === engagement.id && s.item.version === item.version - 1,
      )?.item,
    }))
    .sort(
      (a, b) =>
        a.engagement.name.localeCompare(b.engagement.name) || b.scope.version - a.scope.version,
    );

  const changed = rows.filter((r) => r.status.integrity === 'changed').length;
  const loading = scopes.isLoading || approvals.isLoading;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Governance</span>
          <h1>Scopes</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '72ch', marginTop: -8 }}>
        Every version of every scope. A version is never edited — a change is a new version that
        needs its own approval. Each approval records a hash of the scope it was about, so a scope
        altered afterwards shows here as <strong>changed since approval</strong>.
      </p>

      {changed > 0 ? (
        <p className="login-error" role="alert">
          {changed === 1 ? '1 scope has' : `${changed} scopes have`} changed since approval. The
          stored rules of engagement no longer match what was authorised.
        </p>
      ) : null}

      {scopes.isPartial || approvals.isPartial ? (
        <p className="demo-notice" role="status">
          Some engagements could not be read, so this list may be incomplete.
        </p>
      ) : null}

      <section className="card panel table-wrap">
        {loading ? (
          <p className="muted">Loading scopes…</p>
        ) : scopes.isError ? (
          <p className="login-error" role="alert">
            {scopes.error instanceof ApiError ? scopes.error.message : 'Could not load scopes.'}
          </p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p>No scopes yet.</p>
            <p className="muted">
              {scopes.engagementCount === 0
                ? 'Create an engagement and define its scope.'
                : 'Open an engagement and define its scope.'}
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Engagement</th>
                <th>Version</th>
                <th>Hosts</th>
                <th>Window</th>
                <th>Permits</th>
                <th>Status</th>
                <th>Decided</th>
                <th>Integrity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ scope, engagement, status, previous }) => {
                const key = `${engagement.id}:${scope.version}`;
                const expanded = open === key;
                const integrity = INTEGRITY[status.integrity];
                return (
                  <Fragment key={key}>
                    <tr>
                      <td>
                        <Link to={`/app/engagements/${engagement.id}`} className="table-link">
                          {engagement.name}
                        </Link>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="link-btn"
                          aria-expanded={expanded}
                          onClick={() => setOpen(expanded ? null : key)}
                        >
                          v{scope.version} {expanded ? '▾' : '▸'}
                        </button>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {scope.targets.allowedHosts.join(', ')}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {windowSummary(scope)}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {scope.permits.activeScan ? 'Active' : 'Passive only'}
                        {scope.permits.fuzzing ? ' · fuzzing' : ''}
                      </td>
                      <td>
                        <span className={`status ${STANDING[status.standing].cls}`}>
                          {STANDING[status.standing].label}
                        </span>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {status.decision ? (
                          <>
                            {who(status.decision)}
                            <br />
                            {date(status.decision.at)}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className={integrity.cls} title={integrity.title}>
                          {integrity.label}
                        </span>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="detail-row">
                        <td colSpan={8}>
                          <ScopeDetail scope={scope} previous={previous} status={status} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function ScopeDetail({
  scope,
  previous,
  status,
}: {
  scope: ScopeVersion;
  previous: ScopeVersion | undefined;
  status: ScopeStatus;
}) {
  const changes = previous ? diffScopes(previous, scope) : [];

  return (
    <div className="scope-detail">
      <dl className="kv">
        <dt>Digest now</dt>
        <dd className="mono">{scope.digest ? shortDigest(scope.digest) : '—'}</dd>
        <dt>Digest approved</dt>
        <dd className="mono">
          {status.decision?.scopeDigest ? shortDigest(status.decision.scopeDigest) : '—'}
        </dd>
        <dt>Seed URLs</dt>
        <dd className="mono">{scope.targets.seedUrls.join(', ')}</dd>
        <dt>Excluded paths</dt>
        <dd className="mono">{scope.targets.excludePaths.join(', ') || '—'}</dd>
        <dt>Limits</dt>
        <dd>
          {scope.limits.maxRequestsPerSecond} req/s · {scope.limits.maxDurationMinutes} minutes
        </dd>
        <dt>Authorisation</dt>
        <dd>
          {scope.authorisation.reference} · {scope.authorisation.authorisedByName} ·{' '}
          {date(scope.authorisation.authorisedAt)}
        </dd>
        {status.decision?.note ? (
          <>
            <dt>Approver's note</dt>
            <dd>{status.decision.note}</dd>
          </>
        ) : null}
      </dl>

      <h3 className="detail-title">
        {previous ? `Changes from v${previous.version}` : 'First version'}
      </h3>
      {!previous ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Nothing to compare with.
        </p>
      ) : changes.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Same content as v{previous.version}.
        </p>
      ) : (
        <ul className="diff">
          {changes.map((c) => (
            <li key={c.field}>
              <span className="diff-field">{c.field}</span>
              {c.removed.map((r) => (
                <span key={`-${r}`} className="diff-removed">
                  − {r}
                </span>
              ))}
              {c.added.map((a) => (
                <span key={`+${a}`} className="diff-added">
                  + {a}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
